import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

const alphabet = 'abcdefghijklmnopqrstuvwxyz';

const getRowLabel = (index) => alphabet[index] ?? `${index + 1}.`;

const Row = ({ row, row_name, handleClick, selected }) => {
  if (!row || row.length === 0) return null;

  return (
    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell style={{ padding: 0 }} colSpan={4}>
        <Table size="small" aria-label={row_name}>
          <TableHead
            sx={{
              background: '#fbfbfc',
              '& .MuiTableCell-root.MuiTableCell-head': { fontWeight: 600, color: '#434166' },
            }}
          >
            <TableRow>
              <TableCell sx={{ maxWidth: '5px', px: 0.3 }}>
                {/* FIX #20: decorative icon hidden from screen readers */}
                <KeyboardArrowRightRoundedIcon
                  sx={{ verticalAlign: 'middle' }}
                  aria-hidden="true"
                />
              </TableCell>
              <TableCell align="left" sx={{ minWidth: '13rem' }}>{row_name}</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell align="right">Percent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {row.map((d, i) => {
              const isSelected = d.seriescode === selected;
              return (
                <TableRow
                  key={d.seriescode || `row-${i}`}
                  role="row"
                  hover
                  aria-selected={isSelected}
                  tabIndex={0}
                  selected={isSelected}
                  sx={{
                    '& td': { borderColor: '#eae9ee' },
                    '&:last-child td': { borderBottom: 0 },
                    cursor: 'pointer',
                  }}
                  onClick={(event) => handleClick(event, d.seriescode, d.desc)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleClick(event, d.seriescode, d.desc);
                    }
                  }}
                >
                  <TableCell
                    sx={{
                      color: isSelected ? 'primary.main' : '#545454',
                      px: isSelected ? '7px !important' : '10px !important',
                    }}
                  >
                    {isSelected ? (
                      <CheckCircleRoundedIcon
                        sx={{ fontSize: '1rem', verticalAlign: 'middle' }}
                        aria-label="Selected"
                      />
                    ) : (
                      getRowLabel(i)
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ maxWidth: '15rem', color: isSelected ? 'primary.main' : '#434166' }}
                  >
                    {d.desc}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: isSelected ? 'primary.main' : '#434166', whiteSpace: 'nowrap' }}
                  >
                    {d.valText}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: '#434166', fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    {d?.pct ? `${d.pct}%` : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  );
};

Row.propTypes = {
  row: PropTypes.array,
  row_name: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  selected: PropTypes.string,
};

const RevenuesTable = ({ revenues_data, getHistChartData, seriesCode }) => {
  const handleClick = useCallback(
    (event, code, name) => { getHistChartData(code, name); },
    [getHistChartData]
  );

  const groupedData = useMemo(() => {
    if (!revenues_data) return { BUS: [], GEO: [], PROD: [], TOTAL: [] };
    return revenues_data.reduce((result, item) => {
      const group = item.group;
      if (!result[group]) result[group] = [];
      result[group].push(item);
      return result;
    }, {});
  }, [revenues_data]);

  const { BUS: Business, GEO: Geography, PROD: Product, TOTAL: Total } = groupedData;

  return (
    <TableContainer
      sx={{
        '& th, td': { padding: '2px 6px' },
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        maxHeight: '13rem',
        '&::-webkit-scrollbar-thumb': { background: '#020e4a29', borderRadius: '1px 1px 7px 1px' },
        '&:hover::-webkit-scrollbar-thumb': { background: '#020e4a85', borderRadius: '1px 1px 7px 1px' },
        '&::-webkit-scrollbar-track': { background: '#f2f2f2', borderRadius: '1px 1px 7px 1px' },
        '&::-webkit-scrollbar': { width: '5px !important', height: '5px !important' },
      }}
    >
      <Table aria-label="collapsible revenue">
        <TableBody>
          <Row row={Product} row_name="Product" handleClick={handleClick} selected={seriesCode} />
          <Row row={Business} row_name="Business" handleClick={handleClick} selected={seriesCode} />
          <Row row={Geography} row_name="Geography" handleClick={handleClick} selected={seriesCode} />
          <Row row={Total} row_name="Total" handleClick={handleClick} selected={seriesCode} />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

RevenuesTable.propTypes = {
  revenues_data: PropTypes.array.isRequired,
  getHistChartData: PropTypes.func.isRequired,
  seriesCode: PropTypes.string,
};

export default React.memo(RevenuesTable);
